import { LocalStorageTypes, SessionStorageKeys, SessionStorageTypes, StorageKeys } from "../Types/ExtensionTypes.ts";

export async function set_session_chrome<T extends SessionStorageKeys>(
    object_name: T,
    object: SessionStorageTypes[T]
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        chrome.storage.session.set({ [object_name]: object }, () => {
            if (chrome.runtime.lastError) {
                reject(chrome.runtime.lastError);
            } else {
                resolve();
            }
        });
    });
}

export async function get_session_chrome<T extends SessionStorageKeys>(
    object_name: T
): Promise<SessionStorageTypes[T] | null> {
    return new Promise((resolve) => {
        chrome.storage.session.get([object_name], (result) => {
            if (result[object_name] === undefined) {
                resolve(null);
            } else {
                resolve(result[object_name] as SessionStorageTypes[T]);
            }
        });
    });
}

export async function remove_session_chrome<T extends SessionStorageKeys>(object_name: T): Promise<void> {
    return new Promise((resolve, reject) => {
        chrome.storage.session.remove(object_name, () => {
            if (chrome.runtime.lastError) {
                reject(chrome.runtime.lastError);
            } else {
                resolve();
            }
        });
    });
}

async function set_local_storage(object_name: string, object: unknown) {
    return new Promise<void>((resolve, reject) => {
        try {
            localStorage.setItem(object_name, JSON.stringify(object));
            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

async function get_local_storage<T extends StorageKeys>(object_name: T): Promise<LocalStorageTypes[T] | null> {
    return new Promise((resolve, reject) => {
        try {
            const value = localStorage.getItem(object_name);
            if (value === null) {
                resolve(null);
            } else {
                resolve(JSON.parse(value) as LocalStorageTypes[T]);
            }
        } catch (error) {
            reject(error);
        }
    });
}

async function remove_local_storage(object_name: string) {
    return new Promise<void>((resolve, reject) => {
        try {
            localStorage.removeItem(object_name);
            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

async function set_chrome(object_name: string, object: unknown) {
    return new Promise<void>((resolve, reject) => {
        chrome.storage.local.set({ [object_name]: object }, () => {
            if (chrome.runtime.lastError) {
                reject(chrome.runtime.lastError);
            } else {
                resolve();
            }
        });
    });
}

async function get_chrome<T extends StorageKeys>(object_name: T): Promise<LocalStorageTypes[T] | null> {
    return new Promise((resolve) => {
        chrome.storage.local.get([object_name], function (result) {
            if (result[object_name] === undefined) {
                resolve(null);
            } else {
                resolve(result[object_name] as LocalStorageTypes[T]);
            }
        });
    });
}

async function remove_chrome(object_name: string) {
    return new Promise<void>((resolve, reject) => {
        chrome.storage.local.remove(object_name, () => {
            if (chrome.runtime.lastError) {
                reject(chrome.runtime.lastError);
            } else {
                resolve();
            }
        });
    });
}

export async function set_object<T extends StorageKeys>(object_name: T, object: LocalStorageTypes[T]) {
    if (process.env.APP_TYPE === "ext") {
        return set_chrome(object_name, object);
    } else {
        return set_local_storage(object_name, object);
    }
}

export async function get_object<T extends StorageKeys>(object_name: T): Promise<LocalStorageTypes[T] | null> {
    if (process.env.APP_TYPE === "ext") {
        return get_chrome<T>(object_name);
    } else {
        return get_local_storage<T>(object_name);
    }
}

export async function remove_object(object_name: StorageKeys) {
    if (process.env.APP_TYPE === "ext") {
        return remove_chrome(object_name);
    } else {
        return remove_local_storage(object_name);
    }
}

export async function clear_storage() {
    if (process.env.APP_TYPE === "ext") {
        await chrome.storage.local.clear();
    } else {
        localStorage.clear();
    }
}
